import React, { FunctionComponent, useMemo } from 'react';
import Translate from 'ui/atoms/translate';
import Currency from 'ui/atoms/currency';
import DataRecordTable, { CellTypeEnum } from 'src/libraries/data-record-table';
import {
  AdminApi,
  AdminInvestorList,
  AdminInvestorsListOrderingEnum,
  AdminInvestorsValuesRetrieveFieldEnum,
} from 'api';
import { DataRecordOrdering } from 'ui/types/data-ordering';
import { PaginationProps } from 'ui/molecules/pagination';
import useApiCall from 'src/hooks/use-api-call';
import { Search } from 'src/ui/molecules/filtering';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';

export interface StudioInvestorsTableProps {
  ordering?: DataRecordOrdering;
  onOrderBy?: (fieldName: string) => void;
  investors?: AdminInvestorList[];
  goToDetails: (investorId: string) => void;
  loading?: boolean;
  paginationProps?: PaginationProps;
  hasPermission: boolean;
  setFilterPopupHeight?: (height: number) => void;
}

const StudioInvestorsTable: FunctionComponent<StudioInvestorsTableProps> = (props) => {
  const { ordering, onOrderBy, investors, goToDetails, loading, hasPermission, paginationProps, setFilterPopupHeight } =
    props;

  const { makeAuthenticatedApi } = useApiCall();
  const translate = useTranslateWithStringArgs();
  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  return (
    <DataRecordTable
      ordering={ordering}
      onOrderBy={onOrderBy}
      data={investors}
      loading={loading}
      paginationProps={paginationProps}
      hasPermission={hasPermission}
      onGoToDetails={goToDetails}
      setFilterPopupHeight={setFilterPopupHeight}
      searchComponent={
        <Search input="search" defaultField="search" label={translate('filters.labels.investors')} name="search" />
      }
      cells={[
        {
          title: <Translate name={`studioInvestorsTable.name`} />,
          render: ({ investorName }) => investorName,
          orderingFieldName: AdminInvestorsListOrderingEnum.INVESTOR_NAME_ASC,
          type: CellTypeEnum.text,
          filterName: 'investorName',
          searchFieldName: AdminInvestorsValuesRetrieveFieldEnum.INVESTOR_NAME,
          getFilterData: (params: any) => adminApi.adminInvestorsValuesRetrieve(params),
        },
        {
          title: <Translate name={`studioInvestorsTable.totalAmount`} args={[<>&shy;</>]} />,
          alignContent: 'right',
          render: ({ activeInvestmentsSum }) => activeInvestmentsSum && <Currency>{activeInvestmentsSum}</Currency>,
          orderingFieldName: AdminInvestorsListOrderingEnum.ACTIVE_INVESTMENTS_SUM_ASC,
          type: CellTypeEnum.currency,
          filterName: 'activeInvestmentsSum',
        },
        {
          title: <Translate name={`studioInvestorsTable.distributionPlatform`} />,
          render: ({ distributionPlatformDisplayName }) => distributionPlatformDisplayName,
          orderingFieldName: AdminInvestorsListOrderingEnum.DISTRIBUTION_PLATFORM_DISPLAY_NAME_ASC,
          type: CellTypeEnum.text,
          filterName: 'distributionPlatformDisplayName',
          searchFieldName: AdminInvestorsValuesRetrieveFieldEnum.DISTRIBUTION_PLATFORM_DISPLAY_NAME,
          getFilterData: (params: any) => adminApi.adminInvestorsValuesRetrieve(params),
        },
      ]}
    />
  );
};

export default StudioInvestorsTable;
